'use strict';
const BaseView = require('components-framework/view/base.view');
const withCachedSelectors = require('components-framework/view/with_cached_selectors');
const trackingUtils = require('od-modules/utils/tracking.utils');

const CONTACT_AIRLINES_URLS = {
    FR: 'https://refundclaims.ryanair.com/',
    OE: 'https://refundclaims.ryanair.com/',
    VY: 'https://www.vueling.com/en/we-are-vueling/contact/send-us-a-message',
};

const INVOLUNTARY_STATUS = {
    MMB: 'MMB',
    CONTACT_AIRLINE: 'CONTACT_AIRLINE',
    BANKRUPTCY: 'BANKRUPTCY',
    PENDING_CLASSIFICATION: 'PENDING_CLASSIFICATION',
    MANAGED_BY_TOUCHLESS: 'MANAGED_BY_TOUCHLESS',
    REFUND_WAITING_CONSENT: 'REFUND_WAITING_CONSENT',
};

module.exports = withCachedSelectors(BaseView.extend({
    templateSettings: {
        evaluate: /\{\{#([\s\S]+?)\}\}/g,
        interpolate: /\{\{[^#{]([\s\S]+?)[^}]\}\}/g,
        escape: /\{\{\{([\s\S]+?)\}\}\}/g,
    },

    TRACKING: {
        TRIPDETAILS_CATEGORY: 'tripdetails',
        INCIDENTS_MANAGER_ACTION: 'incidents_manager',
        CANCELLATION_ACTION: 'cancellation',
        CONTACT_ACTION: 'contact',
        REFUND_WAITING_CONSENT_ACTION: 'refund-waiting-consent',
        MANAGED_BY_TOUCHLESS_ACTION: 'touchless',
        MGMT_ACTION: 'mgmt',
        NOTIFICATION_CTA_LABEL_PREFIX: 'click-cta',
        POPUP_OPEN_LABEL_PREFIX: 'click_open',
        POPUP_AIRLINE_CLICK_LABEL_PREFIX: 'click_airline',
        POPUP_REFUND_CLICK_LABEL_PREFIX: 'click_request-refund',
        POPUP_CLOSE_LABEL_PREFIX: 'popup_close',
        REFUND_WAITING_CONSENT_RESOLVED_CTA_PREFIX: 'click_resolved-by-customer',
        REFUND_WAITING_CONSENT_WAIT_CTA_PREFIX: 'click_wait-alternative',
        REFUND_WAITING_CONSENT_REFUND_CTA_PREFIX: 'click_request-refund',
        REFUND_WAITING_CONSENT_CLOSE_LAYER_PREFIX: 'click_go-back',
        NOTIFICATION_EVENT_PARAMS: ['booking', 'status', 'id', 'pag'],
        PNR_EVENT_PARAMS: ['booking', 'status', 'canc', 'id', 'pag'],
        REFUND_WAITING_CONSENT_EVENT_PARAMS: ['id', 'pag'],
    },

    CSS_CLASSES: {
        OPEN: 'open',
        HIDDEN: 'odf-hidden',
    },

    selectors: {
        infoPopUp: '.info_pop_up',
    },

    events: {
        'tap .open_info_pop_up': 'openInfoPopUp',
        'tap .close_info_pop_up': 'closeAllInfoPopUps',
        'tap .manage_booking': 'manageBooking',
        'tap .scheduled_manage_booking': 'scheduledManageBooking',
        'tap .contactAirline_airline_btn': 'openContactAirlineLink',
        'tap .airline_alternative_taken': 'notifyAirlineAlternativeTaken',
        'tap .wait_airline_alternatives': 'waitAirlineAlternatives',
        'tap .request_refund': 'requestRefund',
    },

    initialize({parentWidget, bookingInfo, involuntaryCancellationPNRs, trackingInfo, options}) {
        this.parentWidget = parentWidget;
        this.model.set({
            bookingInfo,
            involuntaryCancellationPNRs,
            trackingInfo,
            renderPNRCards: options.renderPNRCards,
        });
        this.model.setAirlineListFromCMSKey('bankruptcyAirlinesTFList', this.keys.bankruptcyAirlinesTFList);
        this.renderPNRs();
    },

    renderPNRs() {
        const involuntaryCancellationPNRs = this.model.get('involuntaryCancellationPNRs');

        this.$el.empty();
        Odigeo._.forEach(involuntaryCancellationPNRs, (pnrInfo, pnr) => {
            if (pnrInfo.cancellationStatus === INVOLUNTARY_STATUS.CONTACT_AIRLINE) {
                this.renderContactAirlinePNRBox(pnrInfo, pnr);
            } else if (pnrInfo.cancellationStatus === INVOLUNTARY_STATUS.BANKRUPTCY) {
                this.renderBankruptcyAirlinePNRBox(pnrInfo, pnr);
            } else if (pnrInfo.cancellationStatus === INVOLUNTARY_STATUS.PENDING_CLASSIFICATION) {
                this.renderPendingClassificationPNRBox(pnrInfo, pnr);
            } else if (pnrInfo.cancellationStatus === INVOLUNTARY_STATUS.MANAGED_BY_TOUCHLESS) {
                this.renderScheduledChangesPNRBox(pnrInfo, pnr);
            } else if (pnrInfo.cancellationStatus === INVOLUNTARY_STATUS.REFUND_WAITING_CONSENT) {
                this.renderRefundAlternativesPNRBox(pnrInfo, pnr);
            } else {
                this.renderManageBookingPNRBox(pnrInfo, pnr);
            }
        });

        if (!Odigeo.AB.isBookingImportantMessage) {
            const scheduleChangesPNRs = Odigeo._.pick(involuntaryCancellationPNRs,
                (pnrInfo) => pnrInfo.cancellationStatus === INVOLUNTARY_STATUS.MANAGED_BY_TOUCHLESS);
            if (!Odigeo._.isEmpty(scheduleChangesPNRs)) {
                this.parentWidget.updateItineraryScheduleChanges(scheduleChangesPNRs);
            }
        }

        this.updateDomSelectorsToCache();
    },

    renderPendingClassificationPNRBox(pnrInfo, pnr) {
        this.renderHelper('pendingClassification', pnrInfo, pnr);
    },

    renderManageBookingPNRBox(pnrInfo, pnr) {
        this.renderHelper('manageBooking', pnrInfo, pnr);
    },

    renderContactAirlinePNRBox(pnrInfo, pnr) {
        this.renderHelper('contactAirline', pnrInfo, pnr);
    },

    renderBankruptcyAirlinePNRBox(pnrInfo, pnr) {
        this.renderHelper('bankruptcy', pnrInfo, pnr);
    },

    renderScheduledChangesPNRBox(pnrInfo, pnr) {
        const singularOrPlural = this.getSingularOrPlural(pnrInfo);
        const title = this.keys[`MANAGED_BY_TOUCHLESSTitle::${singularOrPlural}`];
        this.renderHelper('scheduledChanges', pnrInfo, pnr, {title});
    },

    renderRefundAlternativesPNRBox(pnrInfo, pnr) {
        this.renderHelper('refundAlternatives', pnrInfo, pnr);
    },

    renderHelper(template, pnrInfo, pnr, options = {}) {
        const {buyer = {}, bookingBasicInfo = {}} = this.model.get('bookingInfo');
        const {formatDate} = Odigeo.Utils;
        const additionalData = {
            keys: this.keys,
            templates: this.templates,
            pnr,
            buyer,
            bookingId: bookingBasicInfo.id,
            imagePath: Odigeo.UserArea.imagePath,
            brandedImagePath: Odigeo.UserArea.brandedImagePath,
            humanTripDate: Odigeo._.partial(Odigeo.Utils.formatDate, _, _, true),
            renderPNRCards: this.model.get('renderPNRCards'),
            formatDate: formatDate,
        };
        const templateData = Object.assign(pnrInfo, additionalData, options);
        const templateRendered = this.templates[template](templateData);
        this.$el.append(templateRendered);
    },

    getSingularOrPlural(pnrInfo) {
        return (Odigeo._.isEmpty(pnrInfo) || (pnrInfo.sectionsCancelled.length <= 1)) ? 'SINGULAR' : 'PLURAL';
    },

    openInfoPopUp(event) {
        event.stopPropagation();
        const pnr = Odigeo._.get(event, 'currentTarget.dataset.pnr');
        const pnrInfo = this.model.get('involuntaryCancellationPNRs')[pnr] || {};
        if (pnrInfo.cancellationStatus === INVOLUNTARY_STATUS.CONTACT_AIRLINE) {
            this.openPNRInfoPopUp(pnr);
            this.trackEmulatedNotificationPNREventHelper(
                pnr,
                this.TRACKING.CONTACT_ACTION,
                this.TRACKING.NOTIFICATION_CTA_LABEL_PREFIX
            );
        } else if (pnrInfo.cancellationStatus === INVOLUNTARY_STATUS.REFUND_WAITING_CONSENT) {
            this.openRefundAlternatives(pnr);
            this.trackEmulatedNotificationPNREventHelper(
                pnr,
                this.TRACKING.REFUND_WAITING_CONSENT_ACTION,
                this.TRACKING.NOTIFICATION_CTA_LABEL_PREFIX
            );
        }
    },

    openRefundAlternatives(pnr) {
        trackingUtils.trackPage('mybooking/involuntary-cancellations/refund-waiting-consent', {funnel: false});
        if (Odigeo.AB.isAlternativeStatus) {
            Odigeo.trigger('involuntary_cancellation:openNewAlternativesFlow');
        } else {
            this.openPNRInfoPopUp(pnr);
        }
    },

    openPNRInfoPopUp(pnr) {
        this.$cachedSelectors.infoPopUp.filter((i, el) => Odigeo._.get(el, 'dataset.pnr') === pnr).toggleClass(this.CSS_CLASSES.OPEN, true);
    },

    closeAllInfoPopUps(event) {
        event.stopPropagation();
        event.preventDefault();
        if (event.target === event.currentTarget) {
            this.$cachedSelectors.infoPopUp.toggleClass(this.CSS_CLASSES.OPEN, false);
            const pnr = Odigeo._.get(event, 'currentTarget.dataset.pnr');
            const pnrInfo = this.model.get('involuntaryCancellationPNRs')[pnr] || {};
            if (pnrInfo.cancellationStatus === INVOLUNTARY_STATUS.REFUND_WAITING_CONSENT) {
                this.trackRefundAlternativesEventHelper(this.TRACKING.REFUND_WAITING_CONSENT_CLOSE_LAYER_PREFIX);
            } else {
                this.trackPNREventHelper(pnr, this.TRACKING.CONTACT_ACTION, this.TRACKING.POPUP_CLOSE_LABEL_PREFIX);
            }
        }
    },

    openContactAirlineLink(event) {
        event.stopPropagation();
        const pnr = Odigeo._.get(event, 'currentTarget.dataset.pnr');
        const carrierCode = Odigeo._.get(this.model.get('involuntaryCancellationPNRs')[pnr], 'validatingCarrier.code');
        const url = carrierCode === 'VY' ? this.keys.contactAirlineAirlineUrlVY : CONTACT_AIRLINES_URLS[carrierCode];
        this.trackPNREventHelper(pnr, this.TRACKING.CONTACT_ACTION, this.TRACKING.POPUP_AIRLINE_CLICK_LABEL_PREFIX);
        if (url) {
            window.open(url, '_blank');
        }
    },

    notifyAirlineAlternativeTaken(event) {
        event.stopPropagation();
        const pnr = Odigeo._.get(event, 'currentTarget.dataset.pnr');
        this.trackRefundAlternativesEventHelper(this.TRACKING.REFUND_WAITING_CONSENT_RESOLVED_CTA_PREFIX);
        this.parentWidget.airlineAlternativeAccepted(pnr);
        this.renderPNRs();
    },

    waitAirlineAlternatives(event) {
        event.stopPropagation();
        this.trackRefundAlternativesEventHelper(this.TRACKING.REFUND_WAITING_CONSENT_WAIT_CTA_PREFIX);
        this.$cachedSelectors.infoPopUp.toggleClass(this.CSS_CLASSES.OPEN, false);
    },

    requestRefund(event) {
        event.stopPropagation();
        const pnr = Odigeo._.get(event, 'currentTarget.dataset.pnr');
        this.trackRefundAlternativesEventHelper(this.TRACKING.REFUND_WAITING_CONSENT_REFUND_CTA_PREFIX);
        this.parentWidget.cashRefundRequested(pnr);
        this.renderPNRs();
    },

    scheduledManageBooking(event) {
        event.stopPropagation();
        const pnr = Odigeo._.get(event, 'currentTarget.dataset.pnr');
        this.trackEmulatedNotificationPNREventHelper(pnr, this.TRACKING.MANAGED_BY_TOUCHLESS_ACTION, this.TRACKING.NOTIFICATION_CTA_LABEL_PREFIX);
        this.parentWidget.scheduleChangesNotificationCTA();
    },

    manageBooking(event) {
        event.stopPropagation();
        const pnr = Odigeo._.get(event, 'currentTarget.dataset.pnr');
        this.trackPNREventHelper(pnr, this.TRACKING.MGMT_ACTION, this.TRACKING.POPUP_OPEN_LABEL_PREFIX);
        this.mmbOpen();
    },

    mmbOpen() {
        this.parentWidget.navigateToMMB();
    },

    trackPNREventHelper(pnr, action, labelPrefix) {
        const carrierCode = Odigeo._.get(this.model.get('involuntaryCancellationPNRs')[pnr], 'validatingCarrier.code');
        const trackingInfo = Object.assign({canc: action}, this.model.get('trackingInfo'));
        trackingInfo.booking = `${trackingInfo.totalSections}-${pnr}`;
        const label = trackingUtils.composeLabel(labelPrefix, this.TRACKING.PNR_EVENT_PARAMS, trackingInfo);
        trackingUtils.trackEvent((action === this.TRACKING.CONTACT_ACTION) ? `${action}-${carrierCode}` : action, label);
    },

    trackEmulatedNotificationPNREventHelper(pnr, cancellationStatus, labelPrefix) {
        const trackingInfo = Object.assign({}, this.model.get('trackingInfo'), {status: cancellationStatus});
        trackingInfo.booking = `${trackingInfo.totalSections}-${pnr}`;
        const label = trackingUtils.composeLabel(labelPrefix, this.TRACKING.NOTIFICATION_EVENT_PARAMS, trackingInfo);
        trackingUtils.trackEvent(this.TRACKING.INCIDENTS_MANAGER_ACTION, label);
    },

    trackRefundAlternativesEventHelper(labelPrefix) {
        const trackingInfo = Object.assign({}, this.model.get('trackingInfo'), {pag: 'refund-waiting-consent'});
        const label = trackingUtils.composeLabel(labelPrefix, this.TRACKING.REFUND_WAITING_CONSENT_EVENT_PARAMS, trackingInfo);
        trackingUtils.trackEventWithCategory(this.TRACKING.TRIPDETAILS_CATEGORY, this.TRACKING.INCIDENTS_MANAGER_ACTION, label);
    },
}));
